import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import type { TablePricingManagerContextColumn } from 'lumen/components/hotel/pricing-assistant/table/table-pricing-manager';
import type { Period } from 'lumen/models';
import { formatMinLosValue } from 'lumen/utils/pricing-assistant/restriction-display-utils';

interface HotelPricingAssistantTableCellsTdHotelMinLosMonthSignature {
  Args: {
    column: TablePricingManagerContextColumn;
    row: { period: Period };
  };
}

export default class HotelPricingAssistantTableCellsTdHotelMinLosMonth extends Component<HotelPricingAssistantTableCellsTdHotelMinLosMonthSignature> {
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];

  get displayValue(): string {
    const value = this.args.row.period.effectiveHotelMinLosWithOverride;

    // formatMinLosValue spells 0 out as "No MinLOS restriction"; the table wants "Open"
    // TODO: switch to a shared general.restriction.none key once it exists in Lokalise
    if (value === 0) {
      return this.intl.t('channel_pricing.restriction.none');
    }

    return formatMinLosValue({ intl: this.intl, value });
  }

  /* The BE recalculates the hotel MinLOS from the rate groups, so it is stale
     while any of them is pushing. */
  get isLoading(): boolean {
    return this.args.row.period.hasRateGroupMinLosActionInProgress;
  }

  get isMissing(): boolean {
    return this.args.row.period.effectiveHotelMinLosWithOverride == null;
  }

  get isPast(): boolean {
    return this.hotelAndMarketService.isPast(this.args.row.period.fromDate);
  }

  <template>
    {{#if this.isLoading}}
      <div class="loading-pulse" data-test-id="loading-pulse"></div>
    {{else}}
      <div
        class="mr-500
          {{if
            this.isPast
            'text-disabled'
            (if this.isMissing 'text-neutral-subdued')
          }}"
        data-test-id="td-hotel-min-los-month"
      >
        {{this.displayValue}}
      </div>
    {{/if}}
  </template>
}
